import React, { FC } from 'react';
import { Accordion, AccordionItem } from '../accordion';
import { FaqCMS, FaqItemCMS, FaqItemGroupCMS } from '@cp-shared-15/apis';

type FaqAdditionalProps = {
    /**
     * Set this to true to open multiple faq items at one time
     */
    openOneAtTime?: boolean;
    /**
     * OnChange for FaqSection
     */
    onChange?: (items: string[]) => void;
    /**
     * Level of header, default one is h2
     */
    headerLevel?: number;
};

type FaqSectionProps = FaqAdditionalProps & {
    /**
     * Array of Faq items which can contain html markup or grouped items
     */
    items: FaqItemCMS[];
};

export const Faq: FC<FaqCMS & FaqAdditionalProps> = ({
    header,
    headerLevel,
    descriptionText,
    items,
    itemGroups,
    openOneAtTime = true,
    onChange,
}) => {
    const headerComponent = () => {
        switch (headerLevel) {
            case 1:
                return <h1 className={'cp-faq-header'}>{header}</h1>;
            case 2:
                return <h2 className={'cp-faq-header'}>{header}</h2>;
            case 3:
                return <h3 className={'cp-faq-header'}>{header}</h3>;
            case 4:
                return <h4 className={'cp-faq-header'}>{header}</h4>;
            case 5:
                return <h5 className={'cp-faq-header'}>{header}</h5>;
            case 6:
                return <h6 className={'cp-faq-header'}>{header}</h6>;
            default:
                return <h2 className={'cp-faq-header'}>{header}</h2>;
        }
    };

    return (
        <section>
            {headerComponent()}
            <div
                dangerouslySetInnerHTML={{
                    __html: `<p className={'cp-faq-description'}>${descriptionText || ''}</p>`,
                }}
            />
            {!items && itemGroups ? (
                <Accordion onChange={onChange}>
                    {itemGroups &&
                        itemGroups.map((itemGroup: FaqItemGroupCMS) => (
                            <AccordionItem key={itemGroup.category} title={itemGroup.category}>
                                <FaqSectionView items={itemGroup.items} openOneAtTime={openOneAtTime} />
                            </AccordionItem>
                        ))}
                </Accordion>
            ) : (
                items && <FaqSectionView onChange={onChange} items={items} openOneAtTime={openOneAtTime} />
            )}
        </section>
    );
};

const FaqSectionView: FC<FaqSectionProps> = ({ items, openOneAtTime = true, onChange }) => {
    const itemsWithRenderedHtml = items.map(({ question, answer }) => ({
        title: question,
        content: <div dangerouslySetInnerHTML={{ __html: answer }} />,
    }));
    return (
        <Accordion openOneAtTime={openOneAtTime} onChange={onChange}>
            {itemsWithRenderedHtml.map(item => (
                <AccordionItem key={item.title} title={item.title}>
                    {item.content}
                </AccordionItem>
            ))}
        </Accordion>
    );
};
